export type SaltMigrationState = "available" | "in_progress" | "migrated" | "retained" | "deleted";

export type SaltMigrationMode =
  | "adapter"
  | "consumer_rewrite"
  | "retained_composition"
  | "delete_if_unreferenced";

export type SaltMigrationWave = 2 | 3 | 4;

export interface SaltMigrationEntry {
  readonly localImport: string;
  readonly wave: SaltMigrationWave;
  readonly migrationMode: SaltMigrationMode;
  readonly owner: string;
  readonly evidenceReference: string;
  readonly saltTarget: string;
  readonly state: SaltMigrationState;
  readonly note: string;
}

const waveMetadata = {
  2: {
    owner: "design-system/forms owner",
    evidenceReference: "docs/receipts/2026-08-13-salt-wave-2-shared-controls-evidence.md",
  },
  3: {
    owner: "design-system/interaction owner",
    evidenceReference: "docs/receipts/2026-08-13-salt-wave-3-compound-controls-evidence.md",
  },
  4: {
    owner: "product design/platform workflow owner",
    evidenceReference: "docs/receipts/2026-08-13-salt-wave-4-flagship-polish-evidence.md",
  },
} as const satisfies Record<
  SaltMigrationWave,
  Pick<SaltMigrationEntry, "owner" | "evidenceReference">
>;

function entry(
  moduleName: string,
  wave: SaltMigrationWave,
  migrationMode: SaltMigrationMode,
  saltTarget: string,
  state: SaltMigrationState,
  note: string,
): SaltMigrationEntry {
  return {
    localImport: `@/components/ui/${moduleName}`,
    wave,
    migrationMode,
    ...waveMetadata[wave],
    saltTarget,
    state,
    note,
  };
}

/**
 * Exhaustive production inventory of `src/components/ui`.
 *
 * Consumer counts are files with a syntactic, exact, direct import of the
 * corresponding local module. Tests, this registry, and prefix matches are not
 * consumers. `verify:salt-migration` recomputes the counts and coverage.
 */
export const saltMigrationRegistry: readonly SaltMigrationEntry[] = [
  entry(
    "action-buttons",
    2,
    "delete_if_unreferenced",
    "Button + StackLayout",
    "deleted",
    "Deleted after the path-resolved graph reached zero importers. It had exactly ONE importer at audit time (SALT-COMPONENT-INDEX §2 row 71); that importer was itself deleted in the dead-file sweep, which is the cascade §6 predicted — 'one pass is not complete'. Its ActionButtons symbol had no other reference anywhere in src.",
  ),
  entry(
    "badge",
    2,
    "adapter",
    "Tag",
    "migrated",
    "Salt Tag owns visual labels; domain status truth stays local.",
  ),
  entry(
    "button",
    2,
    "adapter",
    "Button",
    "migrated",
    "Salt adapter landed; size, asChild, and link-button semantics are removed.",
  ),
  entry(
    "card",
    2,
    "adapter",
    "Card or Panel",
    "migrated",
    "Salt Card and typography own the shared container while domain compositions stay local.",
  ),
  entry(
    "checkbox",
    2,
    "adapter",
    "Checkbox / CheckboxGroup",
    "migrated",
    "Salt Checkbox preserves controlled, uncontrolled, native form, and indeterminate behavior.",
  ),
  entry(
    "currency-input",
    2,
    "retained_composition",
    "NumberInput + FormField",
    "retained",
    "Salt NumberInput owns editing, focus, validation, adornment, and native input refs while local currency parsing stays intact.",
  ),
  entry(
    "form",
    2,
    "adapter",
    "FormField family",
    "migrated",
    "Salt FormField owns label, help, validation, disabled, and read-only context.",
  ),
  entry(
    "icon-action-button",
    2,
    "retained_composition",
    "Button with Salt icon",
    "retained",
    "Salt density now owns the icon-action target; accessible names and disabled tooltip reachability remain mandatory.",
  ),
  entry(
    "input-otp",
    2,
    "delete_if_unreferenced",
    "CompactInput series + FormField",
    "deleted",
    "Deleted after complete production, relative, barrel, and test graph proof reached zero.",
  ),
  entry(
    "input",
    2,
    "adapter",
    "Input + FormField",
    "migrated",
    "Salt adapter forwards the real input ref and complete native attribute contract.",
  ),
  entry(
    "wizard-stepper",
    2,
    "adapter",
    "Stepper + Step",
    "migrated",
    "The one step rail (DR-29). Twelve incompatible rail shapes had accumulated because no rule existed to violate; Salt Step is a non-interactive <li>, so converting a clickable rail costs click-to-jump — decided per flow in docs/audit/WIZARD-INVENTORY.md.",
  ),
  entry(
    "salt-form-field",
    2,
    "adapter",
    "FormField + FormFieldLabel + FormFieldHelperText + Input/MultilineInput",
    "migrated",
    "forwardRef reaches Salt's inputRef/textAreaRef, not the wrapper div — react-hook-form reads the field element, so registered fields submit their values.",
  ),
  entry(
    "international-address",
    2,
    "retained_composition",
    "Salt international-address pattern data contract",
    "retained",
    "Shared country formats and legacy-storage adapters keep all Salt international-address consumers consistent without changing domain persistence contracts.",
  ),
  entry(
    "international-address-form",
    2,
    "retained_composition",
    "Dropdown + Input + FormField + StackLayout",
    "retained",
    "Salt's international-address pattern owns country selection, country-specific labels and order, responsive layout, and visible validation while domain storage remains adapter-owned.",
  ),
  entry(
    "label",
    2,
    "adapter",
    "FormFieldLabel / Label",
    "migrated",
    "Salt label typography preserves explicit native control association.",
  ),
  entry(
    "link",
    2,
    "adapter",
    "Link",
    "migrated",
    "Semantic router and external links replace Button asChild navigation.",
  ),
  entry(
    "password-input",
    2,
    "retained_composition",
    "Input + Button + FormField",
    "retained",
    "Salt-backed Input, Button, and visibility icons preserve reveal state and accessible naming.",
  ),
  entry(
    "percentage-input",
    2,
    "retained_composition",
    "NumberInput + FormField",
    "retained",
    "Salt NumberInput owns editing and validation while the local decimal-on-blur percentage contract remains intact.",
  ),
  entry(
    "progress",
    2,
    "adapter",
    "LinearProgress",
    "migrated",
    "Salt LinearProgress preserves determinate, indeterminate, and accessible value naming.",
  ),
  entry(
    "radio-group",
    2,
    "adapter",
    "RadioButtonGroup / RadioButton",
    "migrated",
    "Salt radio group preserves value, native naming, labels, required state, and keyboard selection.",
  ),
  entry(
    "separator",
    2,
    "adapter",
    "Divider",
    "migrated",
    "Salt Divider preserves decorative or semantic orientation explicitly.",
  ),
  entry(
    "skeleton",
    2,
    "retained_composition",
    "Salt-token loading composition",
    "retained",
    "Salt-token loading composition landed because stable core has no Skeleton export.",
  ),
  entry(
    "switch",
    2,
    "adapter",
    "Switch",
    "migrated",
    "Salt Switch preserves binary state, native form/ref behavior, labels, disabled, and read-only state.",
  ),
  entry(
    "text-action-button",
    2,
    "delete_if_unreferenced",
    "Link / TextAction",
    "deleted",
    "Deleted after complete production, relative, barrel, and test graph proof reached zero.",
  ),
  entry(
    "textarea",
    2,
    "adapter",
    "MultilineInput + FormField",
    "migrated",
    "Salt MultilineInput preserves the native textarea ref, events, resize, and validation contract.",
  ),

  entry(
    "accordion",
    3,
    "consumer_rewrite",
    "Accordion / AccordionGroup",
    "migrated",
    "All consumers use Salt disclosure contracts; single and multi-expand state lives truthfully with each consumer.",
  ),
  entry(
    "alert-dialog",
    3,
    "consumer_rewrite",
    "Dialog",
    "migrated",
    "All consumers now use one declarative Salt alert-dialog contract with destructive confirmation, async state, focus trap, and focus return.",
  ),
  entry(
    "alert",
    3,
    "adapter",
    "Banner",
    "migrated",
    "Salt Banner maps informational and destructive state without color-only meaning.",
  ),
  entry(
    "aspect-ratio",
    3,
    "delete_if_unreferenced",
    "CSS aspect-ratio + Salt layout",
    "deleted",
    "Deleted after complete dependency-graph proof reached zero.",
  ),
  entry(
    "avatar",
    3,
    "consumer_rewrite",
    "Avatar",
    "migrated",
    "Six callers now use Salt name, initials, image, size, and accessible identity directly.",
  ),
  entry(
    "breadcrumb",
    3,
    "delete_if_unreferenced",
    "Link + Text + navigation landmark",
    "deleted",
    "Deleted after complete dependency-graph proof reached zero.",
  ),
  entry(
    "bulk-action-bar",
    3,
    "retained_composition",
    "Toolbar + Button + Divider",
    "retained",
    "Salt Toolbar now owns the shared selection-action surface while count, permissions, and disabled reasons remain local.",
  ),
  entry(
    "calendar",
    3,
    "consumer_rewrite",
    "Salt DatePicker + Calendar via @salt-ds/date-components",
    "migrated",
    "All 33 native type='date' and type='datetime-local' inputs replaced with SaltDatePicker/SaltDateTimePicker wrappers. @salt-ds/date-components + @salt-ds/date-adapters/date-fns installed. LocalizationProvider wired in main.tsx.",
  ),
  entry(
    "carousel",
    3,
    "delete_if_unreferenced",
    "no stable core target",
    "deleted",
    "Deleted after complete dependency-graph proof reached zero.",
  ),
  entry(
    "chart",
    3,
    "delete_if_unreferenced",
    "chart library + Salt tokens/Text/Tooltip",
    "deleted",
    "Unused generic wrapper deleted; live domain charts remain domain-owned.",
  ),
  entry(
    "child-tab-bar",
    3,
    "retained_composition",
    "Tabs / TabBar",
    "retained",
    "Four direct Tabs consumers moved to Salt TabList/TabTrigger; remaining child-route navigation stays domain-owned.",
  ),
  entry(
    "collapsible",
    3,
    "consumer_rewrite",
    "Collapsible",
    "migrated",
    "All consumers use Salt Collapsible with native trigger, panel, focus, and ARIA behavior.",
  ),
  entry(
    "column-editor-dialog.component",
    3,
    "retained_composition",
    "Dialog + CheckboxGroup + StackLayout",
    "retained",
    "The live module has two relative consumers; Salt-backed controls and icons own its interaction chrome.",
  ),
  entry(
    "command",
    3,
    "delete_if_unreferenced",
    "ComboBox + ListBox",
    "deleted",
    "Deleted after both command-palette consumers moved to Salt menu and ComboBox contracts.",
  ),
  entry(
    "context-menu",
    3,
    "delete_if_unreferenced",
    "Menu",
    "deleted",
    "Deleted after complete dependency-graph proof reached zero.",
  ),
  entry(
    "data-filters",
    3,
    "retained_composition",
    "Toolbar + Dropdown / ComboBox",
    "retained",
    "Preserve active-filter truth and clearing.",
  ),
  entry(
    "data-load-error",
    3,
    "retained_composition",
    "Banner + Button",
    "retained",
    "Salt Banner/Button composition preserves retry and exact error-source semantics.",
  ),
  entry(
    "data-table-container",
    3,
    "retained_composition",
    "TableContainer + Panel",
    "retained",
    "Keep title, evidence, loading, empty, and error contract.",
  ),
  entry(
    "data-table-toolbar",
    3,
    "retained_composition",
    "Toolbar",
    "retained",
    "Filter/action priority is preserved and Salt icons own the toolbar chrome.",
  ),
  entry(
    "default-mobile-table-row",
    3,
    "retained_composition",
    "StackLayout + FlexLayout + Text",
    "retained",
    "Default mobile card row for DataTableContainer when no custom renderMobileRow is supplied.",
  ),
  entry(
    "database-error-state",
    3,
    "retained_composition",
    "Banner + Button",
    "retained",
    "Salt Banner/Button now own the shared database recovery state while diagnostic truth stays local.",
  ),
  entry(
    "delete-confirmation-dialog",
    3,
    "retained_composition",
    "Dialog",
    "retained",
    "Item identity and destructive wording are preserved; Salt owns alert and loading chrome.",
  ),
  entry(
    "dialog",
    3,
    "consumer_rewrite",
    "Dialog",
    "migrated",
    "All consumers use the local Salt dialog contract rather than Radix parts.",
  ),
  entry(
    "drawer",
    3,
    "consumer_rewrite",
    "Drawer / DrawerCloseButton",
    "migrated",
    "Eleven consumers now use the direct controlled Salt Drawer contract.",
  ),
  entry(
    "dropdown-field",
    3,
    "adapter",
    "Dropdown / Option",
    "migrated",
    "All scalar select consumers use Salt Dropdown with explicit stored value-to-label mapping.",
  ),
  entry(
    "dropdown-menu",
    3,
    "consumer_rewrite",
    "Menu / MenuTrigger / MenuPanel / MenuItem / MenuGroup / Divider",
    "migrated",
    "All ten production consumers use the direct Salt menu contract.",
  ),
  entry(
    "empty-state",
    3,
    "retained_composition",
    "Card + StackLayout + Text + Button",
    "retained",
    "Salt owns the tertiary empty surface, layout, typography, and primary next action.",
  ),
  entry(
    "enhanced-standard-table",
    3,
    "retained_composition",
    "TableContainer + Table + Paginator",
    "retained",
    "Preserve sort/select/loading/empty contracts.",
  ),
  entry(
    "form-dialog",
    3,
    "retained_composition",
    "Dialog + FormField",
    "retained",
    "Bounded form tasks preserve focus and validation; Salt owns loading chrome.",
  ),
  entry(
    "formatted-value",
    3,
    "delete_if_unreferenced",
    "Text + StatusAdornment",
    "deleted",
    "Deleted after complete dependency-graph proof reached zero.",
  ),
  entry(
    "hover-card",
    3,
    "delete_if_unreferenced",
    "Toggletip / Overlay",
    "deleted",
    "Deleted after complete dependency-graph proof reached zero.",
  ),
  entry(
    "info-tooltip",
    3,
    "retained_composition",
    "Tooltip / Toggletip",
    "retained",
    "The tooltip supplements a visible label and uses the Salt information icon.",
  ),
  entry(
    "list-card",
    3,
    "delete_if_unreferenced",
    "LinkCard / ListBox",
    "deleted",
    "Deleted after complete dependency-graph proof reached zero.",
  ),
  entry(
    "menubar",
    3,
    "delete_if_unreferenced",
    "Menu / MegaMenu",
    "deleted",
    "Deleted after complete dependency-graph proof reached zero.",
  ),
  entry(
    "metric",
    3,
    "retained_composition",
    "Text + StackLayout + Tooltip",
    "retained",
    "Canonical Salt metric pattern owns prominent value hierarchy, label context, semantic status color, orientation, and tabular numerals across dashboards and analytical surfaces.",
  ),
  entry(
    "metric-bar",
    3,
    "retained_composition",
    "Card + FlowLayout + Metric",
    "retained",
    "ONE bordered Salt Card holding a FlowLayout row of Salt Metrics — the 'metrics in one bar' pattern. Distinct from metric-card, which is one bordered Card PER metric arranged in a grid: that remains correct for independently-clickable KPI tiles, while this owns a single analytical band of related metrics. Salt owns the card, the responsive row wrapping, typography and sentiment tokens; which metrics belong together stays domain-owned.",
  ),
  entry(
    "metric-card",
    3,
    "retained_composition",
    "Card / InteractableCard + Text",
    "retained",
    "Salt Card, InteractableCard, typography, density, keyboard behavior, and semantic status tokens now own the shared metric composition.",
  ),
  entry(
    "missing-input-alert",
    3,
    "retained_composition",
    "Banner + Button",
    "retained",
    "Salt Banner/Button now own mechanics while exact missing inputs and remediation stay local.",
  ),
  entry(
    "navigation-menu",
    3,
    "delete_if_unreferenced",
    "MegaMenu / VerticalNavigation",
    "deleted",
    "Deleted after complete shell dependency-graph proof reached zero.",
  ),
  entry(
    "page-actions-bar",
    3,
    "retained_composition",
    "Toolbar + Button",
    "retained",
    "Salt Toolbar/Button now own page-scope commands; ordering, destructive intent, and permissions remain local.",
  ),
  entry(
    "page-header",
    3,
    "retained_composition",
    "StackLayout + Text + Button",
    "retained",
    "Salt StackLayout, H1 typography, Button, and back icon own the shared header while journey truth stays local.",
  ),
  entry(
    "page-toolbar",
    3,
    "delete_if_unreferenced",
    "Toolbar",
    "deleted",
    "Deleted after complete dependency-graph proof reached zero.",
  ),
  entry(
    "pagination",
    3,
    "delete_if_unreferenced",
    "Pagination / Paginator",
    "deleted",
    "Unused duplicate deleted; active table pagination remains in its domain seam.",
  ),
  entry(
    "parent-tab-bar",
    3,
    "retained_composition",
    "Tabs / TabBar",
    "retained",
    "Preserve parent-route wayfinding.",
  ),
  entry(
    "overlay",
    3,
    "adapter",
    "Overlay / OverlayPanel",
    "migrated",
    "Salt owns anchored focus, dismiss, placement, surface, border, and elevation behavior behind the explicit local overlay seam.",
  ),
  entry(
    "popover",
    3,
    "delete_if_unreferenced",
    "Overlay / OverlayPanel",
    "deleted",
    "Deleted after every consumer moved to the explicit local Salt overlay contract.",
  ),
  entry(
    "resizable",
    3,
    "delete_if_unreferenced",
    "SplitLayout",
    "deleted",
    "Deleted after complete dependency-graph proof reached zero.",
  ),
  entry(
    "scroll-area",
    3,
    "retained_composition",
    "native overflow + Salt tokens",
    "retained",
    "Native overflow seam now uses Salt semantic scrollbar/focus tokens with keyboard access; stable core has no ScrollArea.",
  ),
  entry(
    "searchable-select",
    3,
    "consumer_rewrite",
    "ComboBox",
    "migrated",
    "Salt ComboBox owns search, empty, clear, accessible naming, and selection behavior.",
  ),
  entry(
    "select",
    3,
    "delete_if_unreferenced",
    "Dropdown or ComboBox",
    "deleted",
    "Deleted after every consumer moved to a truthful Dropdown or ComboBox contract.",
  ),
  entry(
    "shared-supplier-badge",
    3,
    "retained_composition",
    "Badge + StatusIndicator",
    "retained",
    "Supplier meaning stays domain-owned.",
  ),
  entry(
    "sheet",
    3,
    "delete_if_unreferenced",
    "Drawer / SidePanel",
    "deleted",
    "Deleted after all nine controlled side panels moved to the direct Salt Drawer seam.",
  ),
  entry(
    "slider",
    3,
    "consumer_rewrite",
    "Slider / RangeSlider",
    "migrated",
    "Four consumers use Salt number/onChange contracts; the stale fifth import was removed.",
  ),
  entry(
    "sonner",
    3,
    "delete_if_unreferenced",
    "Toast",
    "deleted",
    "Deleted after every vendor toast consumer and the global host moved to the local Salt Toast seam.",
  ),
  entry(
    "standard-header",
    3,
    "delete_if_unreferenced",
    "StackLayout + Text",
    "deleted",
    "Unused duplicate and its isolated test deleted after graph proof reached zero.",
  ),
  entry(
    "standard-table",
    3,
    "delete_if_unreferenced",
    "TableContainer + Table + Paginator",
    "deleted",
    "Unused barrel export and wrapper deleted after complete graph proof reached zero.",
  ),
  entry(
    "status-badge",
    3,
    "retained_composition",
    "Tag + StatusIndicator",
    "retained",
    "Salt Tag and StatusIndicator own mechanics and semantic color; status labels and truth remain application-owned.",
  ),
  entry(
    "navigation-tab-bar",
    3,
    "retained_composition",
    "Button / Badge / ScrollArea",
    "retained",
    "Salt actions, badges, focus, density, semantic borders, and overflow now own horizontal route navigation.",
  ),
  entry(
    "table-action-cell",
    3,
    "retained_composition",
    "Menu + Button",
    "retained",
    "Accessible action names and row context are preserved; Salt owns the action icon catalog.",
  ),
  entry(
    "table-context",
    3,
    "retained_composition",
    "local state + Salt Table contracts",
    "retained",
    "State coordination remains local.",
  ),
  entry(
    "table-loading",
    3,
    "retained_composition",
    "LinearProgress + StackLayout",
    "retained",
    "Internal table seam now renders a named indeterminate Salt progress state.",
  ),
  entry(
    "table-pagination",
    3,
    "retained_composition",
    "Pagination + Paginator + Dropdown",
    "retained",
    "Salt now owns page navigation, announcement, and page-size selection.",
  ),
  entry(
    "table-selection-header",
    3,
    "retained_composition",
    "Checkbox",
    "retained",
    "Internal table seam now uses the Salt-backed indeterminate select-all control.",
  ),
  entry(
    "table-cell-context",
    3,
    "retained_composition",
    "n/a — React context, renders nothing",
    "retained",
    "Tells descendants they are inside a table cell, so TR-3 can be enforced structurally rather than per call site. StatusBadge reads it and degrades to plain text in any <td>, while keeping its Salt StatusIndicator everywhere else — the rule belongs to the LOCATION, not the component. Renders no DOM and owns no visual semantics; it exists so ~75 StatusBadge call sites and every table added later need remember nothing.",
  ),
  entry(
    "table-sort-header",
    3,
    "retained_composition",
    "Button + Salt sort icons",
    "retained",
    "Internal table seam now uses Salt actions/icons and names the active sort direction.",
  ),
  entry(
    "table-system",
    3,
    "delete_if_unreferenced",
    "Salt table composition barrel",
    "deleted",
    "Never became the table domain seam it claimed: 1 app consumer (EnrollmentsList) against ~60 direct imports of the concrete modules. Its eager re-export pulled the unconsumed CampaignTable, and 11 further modules, into the /enrollments chunk. Consumers now import enhanced-standard-table, table-context and enrollment-table directly.",
  ),
  entry(
    "table",
    3,
    "adapter",
    "TableContainer + Table + THead + TBody + TR + TH + TD + TFoot",
    "migrated",
    "Salt owns responsive overflow, semantics, density, dividers, alignment, and zebra styling behind the local table names.",
  ),
  entry(
    "tabs",
    3,
    "adapter",
    "Tabs / TabList / Tab / TabTrigger / TabPanel",
    "migrated",
    "Local seam composes the complete Salt family while preserving value-only consumer callbacks.",
  ),
  entry(
    "table-toolbar",
    3,
    "retained_composition",
    "FlexLayout / StackLayout + Button",
    "retained",
    "Sanctioned import name for the shared table toolbar (DR-31/TR-4); re-exports the retained data-table-toolbar implementation rather than duplicating it, so DataTableContainer and every DR-04 table still compose exactly one toolbar.",
  ),
  entry(
    "toggle-group",
    3,
    "consumer_rewrite",
    "ToggleButtonGroup",
    "migrated",
    "All consumers use Salt single-selection semantics.",
  ),
  entry(
    "toggle",
    3,
    "consumer_rewrite",
    "ToggleButton",
    "migrated",
    "Salt ToggleButton is available locally; no production standalone consumer requires legacy compatibility.",
  ),
  entry(
    "tooltip",
    3,
    "consumer_rewrite",
    "Tooltip",
    "migrated",
    "All compound Radix consumers now use Salt content/trigger contracts with accessible keyboard triggers.",
  ),
  entry(
    "toast",
    3,
    "adapter",
    "Toast / ToastContent",
    "migrated",
    "The local Salt toast host owns queueing, announcements, actions, validation status, dismissal, and timing while preserving the legacy object-call compatibility seam.",
  ),
  entry(
    "trend-indicator",
    3,
    "retained_composition",
    "Text + Salt directional icons",
    "retained",
    "Internal metric-card support module uses Salt typography and directional icons; direction, polarity, and timeframe stay domain-owned.",
  ),

  entry(
    "advanced-filter-panel",
    4,
    "retained_composition",
    "Drawer + Toolbar + ComboBox / Dropdown",
    "retained",
    "Salt-backed filter mechanics and icons own the shared panel while flagship workflow truth stays local.",
  ),
  entry(
    "alert-callout",
    4,
    "retained_composition",
    "Banner",
    "retained",
    "Salt Banner now owns page callout and shared recovery-state mechanics.",
  ),
  entry(
    "area-chart-card",
    4,
    "delete_if_unreferenced",
    "Card + retained chart",
    "deleted",
    "Unused chart card deleted; live domain chart components remain retained.",
  ),
  entry(
    "campaign-table",
    4,
    // Plan changed from `retained_composition` to `delete_if_unreferenced` — the guard
    // only allows the latter to reach state "deleted", and it is right to: a component
    // planned for retention should not quietly vanish. VERIFIED before the change: the
    // only remaining references to campaign-table anywhere in src are two TESTS that
    // document it AS an orphan (table-components-have-importers.test.ts and
    // enrollments-module-graph.test.ts, the latter asserting the enrollments route does
    // not drag it in). Zero production importers.
    "delete_if_unreferenced",
    "Salt table composition",
    "deleted",
    // Deleted 2026-08-17 by the table-standardization sweep: it was a FORK of the
    // sanctioned table, not a wrapper of it, and DR-04 names EnhancedStandardTable and
    // DataTableContainer as the two allowed renderers. Its call sites moved to those.
    // The registry is marked here rather than the file being resurrected — verify:salt
    // caught the gap, which is the guard doing exactly its job.
    "Deleted: a fork of the sanctioned table. Call sites moved to the DR-04 components.",
  ),
  entry(
    "corporate-table",
    4,
    "delete_if_unreferenced",
    "Salt table composition",
    "deleted",
    "Unused barrel export and wrapper deleted after complete graph proof reached zero.",
  ),
  entry(
    "enrollment-table",
    4,
    "retained_composition",
    "Salt table composition",
    "retained",
    "The internal table composition preserves enrollment semantics and uses the Salt overflow action icon.",
  ),
  entry(
    "sidebar",
    4,
    "delete_if_unreferenced",
    "VerticalNavigation + Drawer / SidePanel",
    "deleted",
    "Unused shadcn sidebar deleted; the active authenticated shell is separate.",
  ),
  entry(
    "stats-card-grid",
    4,
    "retained_composition",
    "GridLayout + MetricCard",
    "retained",
    "Salt GridLayout owns responsive columns and density-aware gaps; consumers retain decision metrics only.",
  ),
  entry(
    "supplier-table",
    4,
    "delete_if_unreferenced",
    "Salt table composition",
    "deleted",
    "Unused barrel export and wrapper deleted after complete graph proof reached zero.",
  ),

  entry(
    "table-column-generators",
    3,
    "delete_if_unreferenced",
    "Local support module",
    "deleted",
    "Deprecated support module deleted after complete dependency-graph proof reached zero.",
  ),
  entry(
    "table-column-types",
    3,
    "delete_if_unreferenced",
    "Local support module",
    "deleted",
    "Deprecated support module deleted after complete dependency-graph proof reached zero.",
  ),
  entry(
    "table-utils",
    3,
    "delete_if_unreferenced",
    "Local support module",
    "deleted",
    "Unused barrel export and support module deleted after complete graph proof reached zero.",
  ),
  entry(
    "use-toast",
    3,
    "consumer_rewrite",
    "Local Salt Toast seam",
    "migrated",
    "Compatibility re-export points to the local Salt Toast seam; new code imports toast directly.",
  ),
  entry(
    "without-colour",
    3,
    "retained_composition",
    "n/a — pure function, renders nothing",
    "retained",
    "Drops colour-bearing declarations from a caller-supplied style when the element is inside a table cell (TR-3). Shared by badge.tsx and status-badge.tsx because BOTH had the same hole in the same shape: the structural in-cell fix removed the CHIP while the caller's own style/textStyle kept the COLOUR. One list, so the two cannot drift. Typography is deliberately excluded — TR-3 removes the swatch, not the type scale. Renders no DOM and owns no visual semantics of its own.",
  ),
];
